import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiDownload } from '../services/api';
import {
  ArrowLeft,
  Download,
  Filter,
  Link2,
  FileText,
  Receipt,
  XCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

interface Transaction {
  id: string;
  statement_id: string;
  transaction_date: string;
  description: string;
  amount: number;
  balance: number | null;
  transaction_type: string | null;
  reference: string | null;
  fee_amount: number | null;
  is_debit: boolean;
  is_fee: boolean;
  matched_invoice_id: string | null;
  matched_expense_id: string | null;
  matched_invoice_number: string | null;
  matched_expense_number: string | null;
  match_confidence: number | null;
  is_matched: boolean;
  is_reconciled: boolean;
  statement_filename: string | null;
}

interface PaginatedResponse {
  items: Transaction[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

const typeColors: Record<string, string> = {
  fee: 'bg-orange-500/10 text-orange-400',
  receipt: 'bg-green-500/10 text-green-400',
  payment: 'bg-red-500/10 text-red-400',
  purchase: 'bg-purple-500/10 text-purple-400',
  transfer: 'bg-blue-500/10 text-blue-400',
  cash: 'bg-yellow-500/10 text-yellow-400',
  interest: 'bg-cyan-500/10 text-cyan-400',
  reversal: 'bg-pink-500/10 text-pink-400',
  credit: 'bg-green-500/10 text-green-400',
  debit: 'bg-red-500/10 text-red-400',
};

function formatCurrency(amount: number | null): string {
  if (amount === null || amount === undefined) return '-';
  const abs = Math.abs(amount);
  const formatted = `R${abs.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
  return amount < 0 ? `-${formatted}` : formatted;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-ZA');
}

type FilterType = 'all' | 'matched' | 'unmatched' | 'debits' | 'credits' | 'fees';

export default function BankStatementTransactions() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchTransactions = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('page_size', '50');
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);

      if (filter === 'matched') params.set('is_matched', 'true');
      if (filter === 'unmatched') params.set('is_matched', 'false');

      // Type-based filters need transaction_type param
      if (filter === 'fees') params.set('transaction_type', 'fee');

      const data = await apiGet<PaginatedResponse>(`/api/bank-statements/transactions?${params}`);
      let items = data.items;

      // Client-side filter for debits/credits (amount-based, not a backend param)
      if (filter === 'debits') items = items.filter((t) => t.amount < 0);
      if (filter === 'credits') items = items.filter((t) => t.amount > 0);

      setTransactions(items);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [page, dateFrom, dateTo, filter]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [dateFrom, dateTo, filter]);

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (filter === 'matched') params.set('is_matched', 'true');
      if (filter === 'unmatched') params.set('is_matched', 'false');
      if (filter === 'fees') params.set('transaction_type', 'fee');

      const blob = await apiDownload(`/api/bank-statements/transactions/export?${params}`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'all_transactions_export.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.message || 'Failed to export CSV');
    } finally {
      setIsExporting(false);
    }
  };

  // Summary stats from current page
  const totalDebits = transactions.filter((t) => t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount), 0);
  const totalCredits = transactions.filter((t) => t.amount > 0).reduce((sum, t) => sum + t.amount, 0);
  const matchedCount = transactions.filter((t) => t.is_matched).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/admin/bank-statements"
          className="p-2 hover:bg-dark-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-400" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">All Transactions</h1>
          <p className="text-gray-400">{total} transactions across all statements</p>
        </div>
      </div>

      {/* Date Range Filter + Export */}
      <div className="flex gap-4 flex-wrap items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="bg-dark-800 border border-dark-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="bg-dark-800 border border-dark-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        {(dateFrom || dateTo) && (
          <button
            onClick={() => { setDateFrom(''); setDateTo(''); }}
            className="px-3 py-2 text-sm bg-dark-800 text-gray-400 hover:text-white rounded-lg transition-colors"
          >
            Clear
          </button>
        )}
        <button
          onClick={handleExportCSV}
          disabled={isExporting}
          className="flex items-center gap-2 px-4 py-2 bg-dark-800 hover:bg-dark-700 text-gray-300 hover:text-white rounded-lg transition-colors disabled:opacity-50 ml-auto"
        >
          <Download className="w-4 h-4" />
          {isExporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-dark-900 rounded-xl border border-dark-800 p-4">
          <p className="text-xs text-gray-500 mb-1">Total Transactions</p>
          <p className="text-lg font-medium text-white">{total}</p>
        </div>
        <div className="bg-dark-900 rounded-xl border border-dark-800 p-4">
          <p className="text-xs text-gray-500 mb-1">Matched</p>
          <p className="text-lg font-medium text-green-400">{matchedCount} <span className="text-sm text-gray-500">on page</span></p>
        </div>
        <div className="bg-dark-900 rounded-xl border border-dark-800 p-4">
          <p className="text-xs text-gray-500 mb-1">Credits (page)</p>
          <p className="text-lg font-medium text-green-400">{formatCurrency(totalCredits)}</p>
        </div>
        <div className="bg-dark-900 rounded-xl border border-dark-800 p-4">
          <p className="text-xs text-gray-500 mb-1">Debits (page)</p>
          <p className="text-lg font-medium text-red-400">{formatCurrency(-totalDebits)}</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap items-center">
        <Filter className="w-4 h-4 text-gray-500" />
        {(['all', 'matched', 'unmatched', 'credits', 'debits', 'fees'] as FilterType[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-colors ${
              filter === f
                ? 'bg-primary-500 text-white'
                : 'bg-dark-800 text-gray-400 hover:text-white'
            }`}
          >
            {f}
          </button>
        ))}
        <span className="text-sm text-gray-500 ml-2">
          {transactions.length} shown
        </span>
      </div>

      {/* Transactions Table */}
      <div className="bg-dark-900 rounded-xl border border-dark-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-800">
                <th className="text-left p-3 text-xs font-medium text-gray-400">Date</th>
                <th className="text-left p-3 text-xs font-medium text-gray-400">Description</th>
                <th className="text-left p-3 text-xs font-medium text-gray-400">Type</th>
                <th className="text-right p-3 text-xs font-medium text-gray-400">Debit</th>
                <th className="text-right p-3 text-xs font-medium text-gray-400">Credit</th>
                <th className="text-right p-3 text-xs font-medium text-gray-400">Balance</th>
                <th className="text-left p-3 text-xs font-medium text-gray-400">Match</th>
                <th className="text-left p-3 text-xs font-medium text-gray-400">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-800">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-gray-500">
                    Loading transactions...
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-gray-500">
                    No transactions found
                  </td>
                </tr>
              ) : (
                transactions.map((tx) => (
                  <tr
                    key={tx.id}
                    className={`hover:bg-dark-800/50 ${
                      tx.is_reconciled ? 'opacity-60' : ''
                    } ${tx.is_matched ? 'bg-green-500/5' : ''}`}
                  >
                    <td className="p-3 text-sm text-gray-300 whitespace-nowrap">
                      {formatDate(tx.transaction_date)}
                    </td>
                    <td className="p-3">
                      <div className="max-w-xs">
                        <p className="text-sm text-white truncate" title={tx.description}>
                          {tx.description}
                        </p>
                        {tx.reference && (
                          <p className="text-xs text-gray-500 truncate" title={tx.reference}>
                            Ref: {tx.reference}
                          </p>
                        )}
                        {tx.fee_amount && (
                          <p className="text-xs text-orange-400">
                            Fee: {formatCurrency(tx.fee_amount)}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      {tx.transaction_type && (
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                            typeColors[tx.transaction_type] || 'bg-gray-500/10 text-gray-400'
                          }`}
                        >
                          {tx.transaction_type}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-right text-sm">
                      {tx.amount < 0 ? (
                        <span className="text-red-400">{formatCurrency(Math.abs(tx.amount))}</span>
                      ) : null}
                    </td>
                    <td className="p-3 text-right text-sm">
                      {tx.amount > 0 ? (
                        <span className="text-green-400">{formatCurrency(tx.amount)}</span>
                      ) : tx.amount === 0 ? (
                        <span className="text-gray-500">R0.00</span>
                      ) : null}
                    </td>
                    <td className="p-3 text-right text-sm text-gray-400">
                      {tx.balance !== null ? formatCurrency(tx.balance) : '-'}
                    </td>
                    <td className="p-3">
                      {tx.is_matched ? (
                        <div className="flex items-center gap-1">
                          <Link2 className="w-3 h-3 text-green-400" />
                          {tx.matched_invoice_id && (
                            <Link
                              to={`/admin/invoices/${tx.matched_invoice_id}`}
                              className="text-xs text-green-400 hover:text-green-300"
                            >
                              <span className="flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                {tx.matched_invoice_number || 'Invoice'}
                              </span>
                            </Link>
                          )}
                          {tx.matched_expense_id && (
                            <Link
                              to={`/admin/expenses/${tx.matched_expense_id}`}
                              className="text-xs text-blue-400 hover:text-blue-300"
                            >
                              <span className="flex items-center gap-1">
                                <Receipt className="w-3 h-3" />
                                {tx.matched_expense_number || 'Expense'}
                              </span>
                            </Link>
                          )}
                          {tx.match_confidence !== null && (
                            <span className="text-xs text-gray-500 ml-1">
                              {Math.round(tx.match_confidence * 100)}%
                            </span>
                          )}
                        </div>
                      ) : tx.is_fee ? (
                        <span className="text-xs text-gray-500">Fee</span>
                      ) : (
                        <XCircle className="w-4 h-4 text-gray-600" />
                      )}
                    </td>
                    <td className="p-3">
                      <Link
                        to={`/admin/bank-statements/${tx.statement_id}`}
                        className="text-xs text-gray-500 hover:text-primary-400 truncate block max-w-[120px]"
                        title={tx.statement_filename || ''}
                      >
                        {tx.statement_filename?.replace('.pdf', '') || 'Statement'}
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-dark-800">
            <p className="text-sm text-gray-400">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-2 bg-dark-800 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
              >
                <ChevronLeft className="w-5 h-5 text-gray-400" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-2 bg-dark-800 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
              >
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
